import { useState, useCallback, useEffect, useMemo } from "react";
import { FiPlus, FiMessageSquare, FiTrash2 } from "react-icons/fi";
import { cn, showToast } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import { Button } from "newui/components/Button";
import Modal from "newui/components/ui/Modal/Modal";
import { TableColumnFilter } from "newui/components/Filters";
import {
	useMyClarifications,
	useDeleteClarification,
} from "../../../../../hooks/usePanelScoringQueries";
import type { PanelClarification } from "../../../../../types/scoring.type";
import TextBoxHTML from "newui/components/TextBoxHTML";
import FallbackImage from "newui/components/FallbackImage/FallbackImage";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox";
import { EvaluationClarificationModal } from "./EvaluationClarificationModal";

interface EvaluationClarificationsTableProps {
	autoOpenForSupplierId?: number | null;
	evalId: number;
	rfxId: number;
	suppliers: Array<{ inviteId: number; supplierLogo: string | null; supplierName: string, }>;
	onAutoOpenHandled?: () => void;
}

// Status ID constants matching backend ClarificationStatus model
const CLARIFICATION_STATUS = {
	DRAFT: 1,
	OPEN: 2,
	CLOSED: 3,
	PENDING: 4,
} as const;

const getStatusLabel = (clarification: PanelClarification): string => {
	if (clarification.is_approved) {
		return "Approved";
	}
	if (clarification.clarification_status_id === CLARIFICATION_STATUS.DRAFT) {
		return "Draft";
	}
	return "Pending Review";
};

const getStatusStyle = (clarification: PanelClarification): string => {
	if (clarification.is_approved) {
		return "bg-green-100 text-green-700";
	}
	if (clarification.clarification_status_id === CLARIFICATION_STATUS.DRAFT) {
		return "bg-gray-100 text-gray-600";
	}
	return "bg-amber-100 text-amber-700";
};

const formatDate = (dateStr: string | null): string => {
	if (!dateStr) {
		return "—";
	}
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-AU", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
};

const ClarificationRow: React.FC<{
	clarification: PanelClarification;
	supplierLogoPath: string;
	supplierName: string;
	onDelete: (clarification: PanelClarification) => void;
	onEdit: (clarification: PanelClarification) => void;
}> = ({ clarification, supplierLogoPath, supplierName, onEdit, onDelete }) => {
	const isDraft = clarification.clarification_status_id === CLARIFICATION_STATUS.DRAFT;

	return (
		<tr
			className={cn(
				"border-b border-gray-100 transition-colors hover:bg-gray-50",
				isDraft && "cursor-pointer"
			)}
			onClick={() => {
				if (isDraft) {
					onEdit(clarification);
				}
			}}
			data-testid={`eval-clarification-row-${clarification.id}`}
		>
			<td className="px-3 py-3 text-sm text-gray-500 font-bold max-w-[300px]">
				<div className="flex rlex-row items-center gap-3">
					<FallbackImage src={supplierLogoPath} className="h-[50px] w-[50px] object-contain" />
					{supplierName}
				</div>
			</td>
			<td className="px-3 py-3 text-sm text-gray-600">
				<TextBoxHTML className="line-clamp-2" html={clarification.clarification_text} />
			</td>
			<td className="px-3 py-3 text-sm text-gray-500">
				{clarification.relates_to || "—"}
			</td>
			<td className="px-3 py-3">
				<span
					className={cn(
						"inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium",
						getStatusStyle(clarification)
					)}
				>
					{getStatusLabel(clarification)}
				</span>
			</td>
			<td className="px-3 py-3 text-sm text-gray-600">
				{clarification.created_by?.name || "—"}
			</td>
			<td className="px-3 py-3 text-xs text-gray-500">
				{formatDate(clarification.created_at)}
			</td>
			<td className="px-3 py-3">
				{isDraft && (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							onDelete(clarification);
						}}
						className="inline-flex items-center rounded p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
						data-testid={`delete-clarification-btn-${clarification.id}`}
					>
						<FiTrash2 size={14} />
					</button>
				)}
			</td>
		</tr>
	);
};

export const EvaluationClarificationsTable: React.FC<EvaluationClarificationsTableProps> = ({
	rfxId,
	evalId,
	suppliers,
	autoOpenForSupplierId,
	onAutoOpenHandled,
}) => {
	const { data: clarifications, isLoading, error } = useMyClarifications(rfxId, evalId);
	const deleteMutation = useDeleteClarification(rfxId, evalId);
	const [showForm, setShowForm] = useState(false);
	const [editingClarification, setEditingClarification] = useState<PanelClarification | null>(null);
	const [deletingClarification, setDeletingClarification] = useState<PanelClarification | null>(null);
	const [supplierFilters, setSupplierFilters] = useState<number[]>([]);
	const [autoOpenSupplierId, setAutoOpenSupplierId] = useState<number | null>(null);

	useEffect(() => {
		if (autoOpenForSupplierId != null) {
			setAutoOpenSupplierId(autoOpenForSupplierId);
			setEditingClarification(null);
			setShowForm(true);
			onAutoOpenHandled?.();
		}
	}, [autoOpenForSupplierId, onAutoOpenHandled]);

	const openCreateForm = useCallback(() => {
		setAutoOpenSupplierId(null);
		setEditingClarification(null);
		setShowForm(true);
	}, []);

	const openEditForm = useCallback((clarification: PanelClarification) => {
		setEditingClarification(clarification);
		setShowForm(true);
	}, []);

	const closeForm = useCallback(() => {
		setShowForm(false);
		setEditingClarification(null);
		setAutoOpenSupplierId(null);
	}, []);

	const handleConfirmDelete = useCallback(() => {
		if (!deletingClarification) {
			return;
		}
		deleteMutation.mutate(deletingClarification.id, {
			onSuccess: () => {
				showToast("Clarification deleted", "Success", true);
				setDeletingClarification(null);
			},
		});
	}, [deletingClarification, deleteMutation]);

	const supplierFilterOptions = useMemo(() =>
		suppliers.map((s) => ({
			value: s.inviteId,
			label: s.supplierName,
		})),
		[suppliers]
	);

	const items = useMemo(() => clarifications ?? [], [clarifications]);

	const filteredItems = useMemo(() => {
		if (supplierFilters.length === 0) {
			return items;
		}
		return items.filter((c) => supplierFilters.includes(c.rfx_invite_id));
	}, [items, supplierFilters]);

	if (isLoading) {
		return (
			<div className="space-y-2" data-testid="eval-clarifications-loading">
				{[1, 2].map((i) => (
					<SkeletonBase key={i} startColor="lightgray" width="100%" height="40px" className="!rounded-lg" />
				))}
			</div>
		);
	}

	if (error) {
		return (
			<p className="text-sm text-red-600" data-testid="eval-clarifications-error">
				Failed to load clarifications.
			</p>
		);
	}

	const thClass = "px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500";

	return (
		<div data-testid="evaluation-clarifications-table">
			<div className="mb-3">
				<div className="flex items-center gap-2 mb-2">
					<FiMessageSquare size={16} className="text-gray-400" aria-hidden="true" />
					<h4 className="text-sm font-medium text-gray-700">
						My Clarifications
					</h4>
					{items.length > 0 && (
						<span className="text-xs text-gray-400">
							({items.length})
						</span>
					)}
				</div>
				<Button
					btntype="primary"
					size="xs"
					onClick={openCreateForm}
					data-testid="add-clarification-btn"
				>
					<FiPlus size={14} aria-hidden="true" />
					Add Clarification
				</Button>
			</div>

			<InstructionsInfoBox inline>
				Clarifications raised here are visible only to you and the RFx Lead.
				They will be approved by the RFx Lead, before being sent to suppliers.
			</InstructionsInfoBox>

			{items.length === 0 ? (
				<div className="rounded-lg border border-dashed border-gray-300 p-6 text-center text-gray-400 text-sm">
					No clarifications raised yet. Use the button above to propose a clarification question.
				</div>
			) : (
				<div className="w-full overflow-x-auto">
					<table className="w-full min-w-[700px] border-collapse text-sm">
						<thead>
							<tr className="border-b border-gray-200">
								<th className="text-left">
									<TableColumnFilter<number>
										label="Supplier"
										align="start"
										className={thClass}
										options={supplierFilterOptions}
										value={supplierFilters}
										onChange={setSupplierFilters}
										isMulti
									/>
								</th>
								<th className={thClass}>Question</th>
								<th className={thClass}>Relates To</th>
								<th className={thClass}>Status</th>
								<th className={thClass}>Created By</th>
								<th className={thClass}>Created</th>
								<th className="w-10"></th>
							</tr>
						</thead>
						<tbody>
							{filteredItems.map((c) => {
								const supplier = suppliers.find((s) => s.inviteId === c.rfx_invite_id);
								return <ClarificationRow
									key={c.id}
									clarification={c}
									supplierName={supplier?.supplierName ?? "Unknown"}
									supplierLogoPath={supplier?.supplierLogo ?? null}
									onEdit={openEditForm}
									onDelete={setDeletingClarification}
								/>
							})}
						</tbody>
					</table>
					{filteredItems.length === 0 && items.length > 0 && (
						<div className="py-6 text-center text-sm text-gray-400">
							No clarifications match the selected filter.
						</div>
					)}
				</div>
			)}

			{showForm && (
				<EvaluationClarificationModal
					rfxId={rfxId}
					evalId={evalId}
					isOpen={showForm}
					suppliers={suppliers}
					editingClarification={editingClarification}
					preSelectedInviteId={autoOpenSupplierId}
					onClose={closeForm}
				/>
			)}

			{/* Delete confirmation modal */}
			{deletingClarification && (
				<Modal
					isOpen={!!deletingClarification}
					onClose={() => setDeletingClarification(null)}
					title="Delete Clarification"
					size="sm"
					modalType="standard"
					iconType="warning"
					closeOnEsc
					closeOnOverlayClick
					primaryButton={{
						text: deleteMutation.isPending ? "Deleting..." : "Delete",
						onClick: handleConfirmDelete,
						btntype: "primary",
						disabled: deleteMutation.isPending,
					}}
					secondaryButton={{
						text: "Cancel",
						onClick: () => setDeletingClarification(null),
					}}
				>
					<p className="text-sm text-gray-600 text-center">
						Are you sure you want to delete this draft clarification? This action cannot be undone.
					</p>
				</Modal>
			)}
		</div>
	);
};
